import { useCallback } from 'react';
import type { InfiniteData } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';

import type { ApiNotification } from '@izimi/api';

import NotificationsQueryKeys from './NotificationsQueryKeys';

export const notificationsQueryKeys = new NotificationsQueryKeys('notifications');

export const useSetPagedNotificationsCache = () => {
  const queryClient = useQueryClient();

  const setPagedNotificationsCache = useCallback(
    (pagedNotifications: InfiniteData<ApiNotification[]>) =>
      queryClient.setQueryData(notificationsQueryKeys.getPagedNotifications(), pagedNotifications),
    [queryClient],
  );

  return setPagedNotificationsCache;
};

export const useGetPagedNotificationsCache = () => {
  const queryClient = useQueryClient();

  const getPagedNotificationsCache = useCallback(() => {
    const pagedNotifications = queryClient.getQueryData<InfiniteData<ApiNotification[]>>(
      notificationsQueryKeys.getPagedNotifications(),
    );
    return pagedNotifications;
  }, [queryClient]);

  return getPagedNotificationsCache;
};

export const useInvalidatePollNotificationsCache = () => {
  const queryClient = useQueryClient();

  const invalidatePollNotificationsCache = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: notificationsQueryKeys.pollNotifications(),
      exact: true,
    });
  }, [queryClient]);

  return invalidatePollNotificationsCache;
};

export const useInvalidatePollVirusScanNotificationsCache = () => {
  const queryClient = useQueryClient();

  const invalidatePollVirusScanNotificationsCache = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: notificationsQueryKeys.pollVirusScanNotifications(),
      exact: true,
    });
  }, [queryClient]);

  return invalidatePollVirusScanNotificationsCache;
};

export const useGetLastNotificationsCache = () => {
  const queryClient = useQueryClient();

  const getLastNotificationsCache = useCallback(() => {
    const notifications = queryClient.getQueryData<ApiNotification[]>(
      notificationsQueryKeys.getLastNotifications(),
    );
    return notifications;
  }, [queryClient]);

  return getLastNotificationsCache;
};

export const useSetLastNotificationsCache = () => {
  const queryClient = useQueryClient();

  const setLastNotificationsCache = useCallback(
    (notifications: ApiNotification[]) =>
      queryClient.setQueryData(notificationsQueryKeys.getLastNotifications(), notifications),
    [queryClient],
  );

  return setLastNotificationsCache;
};

export const useInvalidateLastNotificationsCache = () => {
  const queryClient = useQueryClient();

  const invalidateNotificationsCache = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: notificationsQueryKeys.getLastNotifications(),
      exact: true,
    });
  }, [queryClient]);

  return invalidateNotificationsCache;
};

export const useInvalidateNotificationsCache = () => {
  const queryClient = useQueryClient();

  const invalidateNotificationsCache = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: notificationsQueryKeys.getPagedNotifications(),
      exact: true,
    });
  }, [queryClient]);

  return invalidateNotificationsCache;
};
